import React from "react";
import styled from "styled-components";
import { MessageSquare, Target, TrendingUp, Eye } from "lucide-react";
import { Grid, FlexContainer } from "../../styles/GlobalStyles";
import { StatsCard } from "./StatsCard";
import { EvaluationCard } from "./EvaluationCard";
import { ProblemResolutionCard } from "./ProblemResolutionCard";

const DashboardContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
`;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize["2xl"]};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const PageSubtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background-color: ${({ theme }) => theme.colors.success}15;
  color: ${({ theme }) => theme.colors.success};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const UserProfile = styled(FlexContainer)`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  padding: ${({ theme }) => theme.spacing.lg};
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
`;

const Avatar = styled.div`
  width: 64px;
  height: 64px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.chart.blue},
    ${({ theme }) => theme.colors.chart.purple}
  );
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const UserRole = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const StatsGrid = styled(Grid)`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const EvaluationSection = styled.div`
  grid-column: span 1;
`;

const ProblemSection = styled.div`
  grid-column: span 1;
`;

export const Desenvolvimento: React.FC = () => {
  const statsData = [
    {
      title: "Feedbacks",
      value: "12",
      subtitle: "recebidos",
      footer: "Resumo de atividades →",
      icon: <MessageSquare size={24} />,
      color: "#4A90E2",
    },
    {
      title: "Sem Objetivos",
      value: "01",
      subtitle: "Neste período",
      footer: "Objetivos →",
      icon: <Target size={24} />,
      color: "#F59E0B",
    },
    {
      title: "Planos",
      value: "01",
      subtitle: "criados",
      footer: "Planos →",
      icon: <TrendingUp size={24} />,
      color: "#10B981",
    },
    {
      title: "Avaliações",
      value: "01",
      subtitle: "respondidas",
      footer: "Ver histórico de avaliações →",
      icon: <Eye size={24} />,
      color: "#8B5CF6",
    },
  ];

  return (
    <DashboardContainer>
      <PageHeader>
        <PageTitle>Desenvolvimento</PageTitle>
        <PageSubtitle>
          Acompanhe seu progresso e desenvolvimento profissional
        </PageSubtitle>
      </PageHeader>

      <UserProfile gap="16px">
        <Avatar>J</Avatar>
        <UserInfo>
          <UserName>Júlia</UserName>
          <UserRole>Gestora</UserRole>
          <StatusBadge>Você está avaliando</StatusBadge>
        </UserInfo>
        <div style={{ textAlign: "right", fontSize: "14px", color: "#64748B" }}>
          Você tem mais 13 dias para responder esta avaliação
        </div>
      </UserProfile>

      <StatsGrid columns={4} minColumnWidth="250px">
        {statsData.map((stat, index) => (
          <StatsCard
            key={index}
            title={stat.title}
            value={stat.value}
            subtitle={stat.subtitle}
            footer={stat.footer}
            icon={stat.icon}
            color={stat.color}
          />
        ))}
      </StatsGrid>

      <MainGrid>
        <EvaluationSection>
          <EvaluationCard
            title="Avaliação de Desempenho - Ciclo 1/2024"
            subtitle="Levando em consideração o período de 27/06/2024 até 18/09/2024 como você avalia Júlia nos seguintes critérios:"
            questions={[
              {
                id: "1",
                text: "A pessoa participante irá preencher a avaliação no formato que foi configurado pela pessoa administradora.",
                type: "text",
              },
              {
                id: "2",
                text: "Como você avalia seu desempenho no período?",
                type: "multiple_choice",
                options: [
                  "Necessita melhora",
                  "Atinge parcialmente",
                  "Atinge o esperado",
                  "Excede o esperado",
                  "Excede muito o esperado",
                ],
              },
            ]}
            currentStep={4}
            totalSteps={13}
          />
        </EvaluationSection>

        <ProblemSection>
          <ProblemResolutionCard />
        </ProblemSection>
      </MainGrid>
    </DashboardContainer>
  );
};
